import React, { useEffect, useRef, useState, useMemo } from 'react';
import './index.scss';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TocMenuProps {
  /** 渲染内容的容器选择器，TocMenu 会从中查找 heading 元素 */
  containerSelector: string;
}

/** 将标题文本转为可用的锚点 id */
function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[\s]+/g, '-')
    .replace(/[^\w\u4e00-\u9fa5-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    || 'heading';
}

const TocMenu: React.FC<TocMenuProps> = ({ containerSelector }) => {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [collapsed, setCollapsed] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const headingElementsRef = useRef<Map<string, IntersectionObserverEntry>>(new Map());

  // 解析容器内的 heading 生成目录
  useEffect(() => {
    // 等待 MDEditor 渲染完成
    const timer = setTimeout(() => {
      const container = document.querySelector(containerSelector);
      if (!container) {
        setItems([]);
        return;
      }

      const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
      const tocItems: TocItem[] = [];
      const ids = new Set<string>();

      headings.forEach((el) => {
        const heading = el as HTMLElement;
        const text = heading.textContent || '';
        if (!text.trim()) return;

        let id = slugify(text);
        // 处理重复 id
        if (ids.has(id)) {
          let suffix = 2;
          while (ids.has(`${id}-${suffix}`)) suffix++;
          id = `${id}-${suffix}`;
        }
        ids.add(id);
        heading.id = id;

        const level = parseInt(el.tagName[1], 10);
        tocItems.push({ id, text, level });
      });

      setItems(tocItems);

      // 默认激活第一个
      if (tocItems.length > 0) {
        setActiveId(tocItems[0].id);
      }
    }, 200); // 等 MDEditor 渲染 DOM

    return () => clearTimeout(timer);
  }, [containerSelector]);

  // IntersectionObserver 监控滚动高亮
  useEffect(() => {
    if (items.length === 0) return;

    // 清理旧的 observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const container = document.querySelector(containerSelector);
    if (!container) return;

    headingElementsRef.current = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          headingElementsRef.current.set(entry.target.id, entry);
        });

        // 找出所有可见的 heading，取最上面的那个
        const visibleEntries: IntersectionObserverEntry[] = [];
        headingElementsRef.current.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleEntries.push(entry);
          }
        });

        if (visibleEntries.length > 0) {
          // 取 top 值最小的（最靠上的可见 heading）
          visibleEntries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(visibleEntries[0].target.id);
        } else {
          // 没有可见的，找最接近当前滚动位置以上的
          const allEntries = Array.from(headingElementsRef.current.entries());
          const scrollTop = window.scrollY;
          let closestId = activeId;
          let closestDist = Infinity;

          for (const [, entry] of allEntries) {
            const dist = Math.abs(entry.boundingClientRect.top);
            if (dist < closestDist) {
              closestDist = dist;
              closestId = entry.target.id;
            }
          }
          setActiveId(closestId);
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      },
    );

    // 观测所有 heading
    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [items, containerSelector]);

  // 点击锚点滚动
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 没有内容时不渲染
  if (items.length === 0) return null;

  return (
    <nav className={`toc-menu ${collapsed ? 'toc-menu--collapsed' : ''}`}>
      <div className="toc-menu__header">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6h16M4 12h16M4 18h12" />
          <path d="M20 12l-3-3M20 12l-3 3" />
        </svg>
        <span className="toc-menu__title">目录</span>
        <button
          className="toc-menu__toggle"
          onClick={() => setCollapsed(v => !v)}
          title={collapsed ? '展开目录' : '收起目录'}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {collapsed ? (
              <polyline points="9 18 15 12 9 6" />
            ) : (
              <polyline points="15 18 9 12 15 6" />
            )}
          </svg>
        </button>
      </div>
      {!collapsed && (
        <ul className="toc-menu__list">
          {items.map((item) => (
            <li
              key={item.id}
              className={`toc-menu__item toc-menu__item--h${item.level} ${activeId === item.id ? 'toc-menu__item--active' : ''}`}
            >
              <button
                className="toc-menu__link"
                onClick={() => handleClick(item.id)}
              >
                {item.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
};

export default TocMenu;
